import { berlinUhrzeit, berlinZeitpunkt, type Personenangabe, type Rolle } from "@dj/domain/gemeinsam";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "react-aria-components";
import { useNavigate, useParams } from "react-router";
import { Abweisung } from "../design/Hinweis";
import { meldungsIdAnzeige } from "../design/Kennung";
import { PersonMitBezeichnung } from "../design/Person";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument, type Zeile } from "../plattform/lesemodelle";
import { anrufen, navigieren, positionMessen } from "../plattform/position";
import {
  personAus, positionAus, useDrueckjagdSammlung, useMeinEintrag, useMeineAufgaben, useRevierSammlung, type Rahmen,
} from "./daten";
import { eingehenderNotruf, entfernungVomGeraet, rettungspunkteAus, rettungspunktZum } from "./eingehend";
import { Notrufband, NotrufEingang, Quittiert } from "./EingehenderNotruf";

/**
 * Lagebild (oberflaeche.md §6, design/README §9.9; Domänenmodell §13, F-184): Jagdherr und Jagdleiter sehen auf einen
 * Blick, wo die Drückjagd steht — Zähler „Notruf“ (öffnet die Notrufe, `Notrufe.tsx`), „Hund vermisst“, „Offene
 * Suchen“, „Kontingentstand“; Bereiche „Vermisste Hunde“, „Schüsse“, je Anstellgruppe und Treibergruppe ein Bereich mit ihren Personen (nach §2: Name und
 * Bezeichnung bzw. ohne Anrede Symbol und Beschreibung) und dem Anstell- bzw. Treibergruppenstatus,
 * „Unkontrollierte Mitwirkende“; unten der Auslöser der Leitung (§4.3). Gelesen wird das Lagebild des eigenen Reviers
 * und die geteilten Teile (offene Notrufe, vermisste Hunde, ADR-0011).
 *
 * Solange ein Notruf noch von niemandem quittiert ist, zeigt das Lagebild an seiner Stelle den eingehenden Notruf
 * (`alertdialog` „Notruf“, §5); nach „Quittieren“ steht über dem Lagebild die Bestätigung, bis zur nächsten Berührung,
 * mindestens 3 s (design/README §9.3).
 */
export function Lagebild({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { revier, rollen } = useMeinEintrag(id);
  const aufgaben = useMeineAufgaben(id);
  const notrufe = useDrueckjagdSammlung(id, "offene_notrufe");
  const vermisst = useDrueckjagdSammlung(id, "vermisste_hunde");
  const notfallkontakte = useDrueckjagdSammlung(id, "notfallkontakte");
  const anstellung = useRevierSammlung(id, revier, "lagebild_anstellung");
  const treiben = useRevierSammlung(id, revier, "lagebild_treibergruppen");
  const schuesse = useRevierSammlung(id, revier, "lagebild_schuesse");
  // Den Zeitpunkt eines Schusses führt das Lagebild nicht (lesemodelle.md §4); die Tabelle (oberflaeche.md §6) nimmt ihn
  // aus der Jagddokumentation, die dieselbe Leitung liest.
  const dokumentierteSchuesse = useRevierSammlung(id, revier, "jagddokumentation_schuesse");
  const suchen = useRevierSammlung(id, revier, "lagebild_offene_suchen");
  const unkontrolliert = useRevierSammlung(id, revier, "lagebild_unkontrolliert");
  const kontingente = useRevierSammlung(id, revier, "kontingentstand");
  const anstellgruppen = useRevierSammlung(id, revier, "anstellgruppen");
  const treibergruppen = useRevierSammlung(id, revier, "treibergruppen");
  const [abgewiesen, setAbgewiesen] = useState<string | null>(null);
  const rettungspunkte = useDrueckjagdSammlung(id, "rettungspunkte");
  const [quittiertHier, setQuittiertHier] = useState<ReadonlySet<string>>(() => new Set());
  const [bestaetigt, setBestaetigt] = useState<{ notfallart: string; stand: string | null; zeit: Date } | null>(null);
  const leitung = rollen.includes("Jagdherr") || rollen.includes("Jagdleiter");
  const eingehend = leitung ? eingehenderNotruf(notrufe, aufgaben.person?.Kennung ?? null, quittiertHier) : null;
  const sperreEingang = useSperre("kritisch", eingehend?._id ?? null, !!eingehend);
  const geraet = useGeraetepositionFuer(eingehend?._id ?? null);
  const melderKennungen = (eingehend?.["_kennungen"] as Record<string, string> | undefined) ?? {};
  const melderEintrag = useDokument(melderKennungen["Revier des Melders"] && melderKennungen["Melder"]
    ? `reviere/${melderKennungen["Revier des Melders"]}/teilnehmeruebersicht/${melderKennungen["Melder"]}` : null);
  useBisZurNaechstenBeruehrung(bestaetigt, () => setBestaetigt(null));

  const quittieren = async (n: Zeile) => {
    setQuittiertHier((s) => new Set(s).add(n._id));
    setBestaetigt({ notfallart: String(n["Notfallart"]), stand: n["Stand"] ? String(n["Stand"]) : null, zeit: new Date() });
    const a = await befehl("NotrufQuittieren", { Notruf: n._id });
    if (a.ok || a.netzfehler) return;
    setAbgewiesen(abweisungsgrund(a));
    setBestaetigt(null);
    setQuittiertHier((s) => new Set([...s].filter((x) => x !== n._id)));
  };
  const zuBereich = (bereich: string) => document.getElementById(bereich)?.scrollIntoView({ block: "start" });
  const notruf112 = notfallkontakte.find((k) => k["Art"] === "Notruf");
  const gesperrteKontingente = kontingente.filter((k) => k["Status"] && k["Status"] !== "frei").length;
  const gruppenNamen = [...new Set([...anstellgruppen.map((g) => String(g["Anstellgruppe"])),
    ...anstellung.map((a) => a["Anstellgruppe"]).filter((g): g is string => typeof g === "string")])].sort((a, b) => a.localeCompare(b, "de"));
  const ohneGruppe = anstellung.filter((a) => !a["Anstellgruppe"]);
  const nachStand = (a: Zeile, b: Zeile) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0);

  if (eingehend) {
    const telefon = (melderEintrag.daten?.["Telefonnummer"] as string | undefined) ?? (notruf112?.["Anruf"] as string | undefined);
    const position = positionAus(eingehend["Position"]);
    return rahmen({
      drueckjagdId: id, name: "Lagebild", art: "notruf-ein",
      children: (
        <NotrufEingang notruf={eingehend} entfernung={entfernungVomGeraet(eingehend, geraet)}
          rettungspunkt={rettungspunktZum(eingehend, rettungspunkteAus(rettungspunkte))?.text ?? null} gesperrt={sperreEingang}
          onQuittieren={() => void quittieren(eingehend)} onNavigation={position ? () => navigieren(position) : undefined}
          onAnrufen={telefon ? () => anrufen(telefon) : undefined} />
      ),
    });
  }

  // Lagebild (design/README §9.9, Referenz `#s-lagebild`): Zähler „Notruf“ und „Hund vermisst“ fest oben, darunter die
  // Bereiche im Scrollbereich (zuerst die vermissten Hunde, nur wenn es welche gibt – der Zähler sagt „0“ schon –, dann
  // Schüsse und die übrigen Zähler; die Notrufe öffnet der Zähler „Notruf“ als eigene Ansicht, Referenz `#s-notrufe`), unten
  // der Auslöser; nach „Quittieren“ deckt die Bestätigung alles über „Zurück“ (§9.3).
  return rahmen({
    drueckjagdId: id, name: "Lagebild", art: "leitung", ausloeser: true,
    oben: (
      <div className="zaehler-fix">
        <Zaehler wort="Notruf" zahl={notrufe.length} art={notrufe.length ? "zaehler--alarm" : undefined} onPress={() => navigate(`/drueckjagd/${id}/notrufe`)} />
        {/* ohne vermissten Hund gibt es keinen Bereich, zu dem der Zähler führt: dann nicht bedienbar (Bericht N10) */}
        <Zaehler wort="Hund vermisst" zahl={vermisst.length} art={vermisst.length ? "zaehler--hund" : undefined} isDisabled={vermisst.length === 0}
          onPress={() => zuBereich("lagebild-vermisst")} />
      </div>
    ),
    ueber: bestaetigt ? <Quittiert {...bestaetigt} /> : undefined,
    status: notrufe.length ? <Notrufband notruf={[...notrufe].sort((a, b) => zeit(a["Zeitpunkt"]) - zeit(b["Zeitpunkt"]))[0]!} /> : undefined,
    children: (
      <>
        <Abweisung grund={abgewiesen} />

        {vermisst.length ? (
          <section className="liste-bereich platte" role="region" aria-labelledby="lagebild-vermisst">
            <h2 className="kopf" id="lagebild-vermisst">Vermisste Hunde</h2>
            {[...vermisst].sort((a, b) => zeit(a["seit"]) - zeit(b["seit"])).map((v) => <VermissterHund key={v._id} hund={v} />)}
          </section>
        ) : null}

        <section className="liste-bereich platte" role="region" aria-labelledby="lagebild-schuesse">
          <h2 className="kopf" id="lagebild-schuesse">Schüsse</h2>
          <table className="tabelle tabelle--breit" role="table">
            <thead role="rowgroup"><tr role="row">{["Schuss", "Schütze", "Stand", "Zeitpunkt", "Ansprache", "Schussausgang"].map((s) => <th key={s} scope="col" role="columnheader">{s}</th>)}</tr></thead>
            <tbody role="rowgroup">
              {[...schuesse].sort((a, b) => String(a["Schuss"]).localeCompare(String(b["Schuss"]))).map((s) => (
                <tr key={s._id} role="row">
                  <td role="cell" className="kennung">{meldungsIdAnzeige(s["Schuss"])}</td>
                  <td role="cell">{personAus(s["Schütze"])?.Name ?? "—"}</td>
                  <td role="cell">{String(s["Stand"] ?? "—")}</td>
                  <td role="cell">{zeitpunktDes(s, dokumentierteSchuesse)}</td>
                  <td role="cell">{[s["Wildart"], s["Altersklasse"]].filter(Boolean).join(" ") || "—"}</td>
                  <td role="cell">{s["storniert"] ? "storniert" : String(s["Schussausgang"] ?? "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <div className="zaehler-reihe">
          <Zaehler wort="Offene Suchen" zahl={suchen.length} onPress={() => navigate(`/drueckjagd/${id}/offene-suchen`)} />
          <Zaehler wort="Kontingentstand" zahl={gesperrteKontingente} onPress={() => navigate(`/drueckjagd/${id}/kontingentstand`)} />
        </div>

        {gruppenNamen.map((g) => {
          const gruppe = anstellgruppen.find((x) => x["Anstellgruppe"] === g);
          const ansteller = ((gruppe?.["Ansteller"] as Personenangabe[] | undefined) ?? []);
          return (
            <Gruppe key={`ag-${g}`} name={`Anstellgruppe ${g}`} symbol="ansteller">
              {ansteller.map((p) => <PersonEintrag key={p.Kennung} person={p} rollen={["Ansteller"]} bezug={{ anstellgruppe: g }} />)}
              {anstellung.filter((a) => a["Anstellgruppe"] === g).sort(nachStand).map((a) => <StandEintrag key={a._id} zeile={a} />)}
            </Gruppe>
          );
        })}
        {ohneGruppe.length ? (
          <Gruppe name="Stände" symbol="stand">
            {[...ohneGruppe].sort(nachStand).map((a) => <StandEintrag key={a._id} zeile={a} />)}
          </Gruppe>
        ) : null}

        {[...treibergruppen].sort((a, b) => String(a["Treibergruppe"]).localeCompare(String(b["Treibergruppe"]), "de")).map((tg) => {
          const name = String(tg["Treibergruppe"]);
          const status = treiben.find((t) => t["Treibergruppe"] === name);
          const fuehrer = personAus(tg["Treiberführer"]);
          const mitglieder = ((tg["Mitglieder"] as Personenangabe[] | undefined) ?? []).filter((p) => p.Kennung !== fuehrer?.Kennung);
          const durchgeh = (tg["Durchgeh-Hundeführer"] as Personenangabe[] | undefined) ?? [];
          return (
            <Gruppe key={`tg-${name}`} name={`Treibergruppe ${name}`} symbol="treiberfuehrer">
              <p className="zeile__u">{[status?.["Treibergruppenstatus"] ?? "nicht gestartet",
                status?.["seit"] instanceof Date ? `seit ${berlinZeitpunkt(status["seit"])}` : null].filter(Boolean).join(" · ")}</p>
              {fuehrer ? <PersonEintrag person={fuehrer} rollen={["Treiberführer"]} bezug={{ treibergruppe: name }} /> : null}
              {mitglieder.map((p) => <PersonEintrag key={p.Kennung} person={p} rollen={["Treiber"]} bezug={{ treibergruppe: name }} />)}
              {durchgeh.map((p) => <PersonEintrag key={`d-${p.Kennung}`} person={p} rollen={["Durchgeh-Hundeführer"]} bezug={{ treibergruppe: name }} />)}
            </Gruppe>
          );
        })}

        <section className="liste-bereich platte" role="region" aria-labelledby="lagebild-unkontrolliert">
          <h2 className="kopf" id="lagebild-unkontrolliert">Unkontrollierte Mitwirkende</h2>
          {unkontrolliert.map((u) => {
            const p = personAus(u["Person"]);
            return p ? (
              <article key={u._id} className="zeile" aria-label={p.Name}>
                <span className="zeile__t">{p.Name}</span>{" "}
                <span className="zeile__u">{[u["führt Waffe"] ? "führt Waffe" : null, String(u["Stand der Nachweiskontrolle"] ?? "")].filter(Boolean).join(" · ")}</span>
              </article>
            ) : null;
          })}
          {unkontrolliert.length === 0 ? <p className="zeile__u">—</p> : null}
        </section>
      </>
    ),
  });
}

/** Eine Messung der Geräteposition je eingehendem Notruf (Entfernung mit Richtung); ohne Position `null`. */
function useGeraetepositionFuer(notrufId: string | null): { Breite: number; Länge: number } | null {
  const [position, setPosition] = useState<{ Breite: number; Länge: number } | null>(null);
  useEffect(() => {
    if (!notrufId) return undefined;
    let aktiv = true;
    void positionMessen().then((m) => { if (aktiv && m) setPosition(m.position); });
    return () => { aktiv = false; };
  }, [notrufId]);
  return position;
}

/** Bestätigung bis zur nächsten Berührung, mindestens 3 s (design/README §9.3). */
function useBisZurNaechstenBeruehrung(wert: unknown, ende: () => void): void {
  useEffect(() => {
    if (!wert) return undefined;
    let beruehrt: (() => void) | null = null;
    const t = window.setTimeout(() => {
      beruehrt = () => ende();
      document.addEventListener("pointerdown", beruehrt, { once: true });
    }, 3000);
    return () => {
      window.clearTimeout(t);
      if (beruehrt) document.removeEventListener("pointerdown", beruehrt);
    };
  }, [wert]);
}

function zeitpunktDes(schuss: Zeile, dokumentiert: Zeile[]): string {
  const z = dokumentiert.find((d) => d["Schuss"] === schuss["Schuss"])?.["Zeitpunkt"];
  return z instanceof Date ? berlinZeitpunkt(z) : "—";
}

function zeit(w: unknown): number {
  return w instanceof Date ? w.getTime() : 0;
}

function Zaehler({ wort, zahl, art, isDisabled, onPress }: { wort: string; zahl: number; art?: string; isDisabled?: boolean; onPress: () => void }) {
  const kennung = `zaehler-${wort.replace(/\s+/g, "-")}`;
  return (
    <Button className={art ? `zaehler ${art}` : "zaehler"} aria-labelledby={`${kennung}-b`} aria-describedby={`${kennung}-z`} isDisabled={isDisabled} onPress={onPress}>
      <span className="zaehler__z" id={`${kennung}-z`}>{zahl}</span>
      <span className="zaehler__b" id={`${kennung}-b`}>{wort}</span>
      <Symbol name="weiter" className="zaehler__pfeil" />
    </Button>
  );
}

function Gruppe({ name, symbol, children }: { name: string; symbol: string; children: ReactNode }) {
  const kopf = `lagebild-${name.replace(/[^A-Za-z0-9]+/g, "-")}`;
  return (
    <section className="liste-bereich platte" role="region" aria-labelledby={kopf}>
      <h2 className="kopf gruppenkopf" id={kopf}><Symbol name={symbol} />{name}</h2>
      {children}
    </section>
  );
}

function PersonEintrag({ person, rollen, bezug }: { person: Personenangabe; rollen: Rolle[]; bezug: { anstellgruppe?: string; treibergruppe?: string } }) {
  return (
    <article className="zeile" aria-label={person.Name}>
      <PersonMitBezeichnung person={person} rollen={rollen} bezug={bezug} />
    </article>
  );
}

function StandEintrag({ zeile }: { zeile: Zeile }) {
  const titel = `Stand ${String(zeile["Stand"])}`;
  const schuetze = personAus(zeile["Schütze"]);
  return (
    <article className="zeile" aria-label={titel}>
      <span className="zeile__t">{titel}</span>{" "}
      {schuetze ? <PersonMitBezeichnung person={schuetze} rollen={["Schütze"]} bezug={{ stand: String(zeile["Stand"]) }} /> : <span>unbesetzt</span>}
      <span className="zeile__u">{String(zeile["Anstellstatus"] ?? "—")}</span>
    </article>
  );
}

/**
 * Vermisster Hund in der Form der Referenz (`#s-lagebild`, `#s-vermisst`): Name, darunter Hundeführer und seit wann,
 * nach einer Sichtung von wem – zwei Zeilen, damit auch auf kleinen Geräten bei 200 % ein ganzer Eintrag im Bild steht
 * (Mindestinhalt README §4.4). Beschreibung und letzte Position zeigen die Abläufe „Hund gesichtet“/„Hund zurück“.
 */
function VermissterHund({ hund }: { hund: Zeile }) {
  const name = String(hund["Hund"]);
  const fuehrer = personAus(hund["Hundeführer"]);
  const gesichtet = personAus(hund["gesichtet von"]);
  const unter = [fuehrer?.Name, hund["seit"] instanceof Date ? `seit ${berlinUhrzeit(hund["seit"])}` : null, gesichtet ? `gesichtet von ${gesichtet.Name}` : null];
  return (
    <article className="zeile" aria-label={name}>
      <span className="zeile__t">{name}</span>{" "}
      <span className="zeile__u">{unter.filter(Boolean).join(" · ")}</span>
    </article>
  );
}
